// src/pages/ESG/Report/RealReport.jsx
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import useScrollAnimation from '../../../hooks/useScrollAnimation';
import { AnimatedSection } from '../../../components/common/AnimatedSection';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding-top: 10%;
  padding-bottom: 8rem;
  @media (max-width: 768px) {
    padding-top: 30%;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 3rem;
  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  padding: 3rem 0;
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
  
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111111;
  margin-bottom: 1.5rem;
`;

const PageSubtitle = styled.p`
  font-size: 1.1rem;
  color: #6b7280;
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const SectionBox = styled.div`
  border: 1px solid #f9fafb;
  border-radius: 12px;
  padding: 3.5rem 4rem;
  margin-bottom: 2rem;
  background-color: #f9fafb;

  @media (max-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #111;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 0.5rem;
  .required { color: #ef4444; margin-left: 0.25rem; }
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  min-height: 150px;
  font-size: 1rem;
`;

const FileInput = styled.input`
  padding: 0.5rem 0;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 2rem;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
`;

const SubmitButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

const SubmitButton = styled.button`
  margin-top: 2rem;
  background-color: #3b82f6;
  color: white;
  padding: 0.8rem 2rem;
  border: none;
  border-radius: 9999px;
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  &:hover { background-color: #2563eb; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

function RealReport() {
  const [headerRef, headerVisible] = useScrollAnimation();
  const [submitting, setSubmitting] = useState(false);
  // 프록시(setupProxy.js) 사용 시 '' 권장, 배포에서 별도 도메인이면 REACT_APP_API_BASE 지정
  const API = process.env.REACT_APP_API_BASE || '';

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const validate = (fd) => {
    const name = fd.get('reporterName')?.trim();
    const email = fd.get('reporterEmail')?.trim();
    const phone = fd.get('reporterPhone')?.trim();
    const targetType = fd.get('targetType');
    const title = fd.get('title')?.trim();
    const content = fd.get('content')?.trim();

    if (!name) return '성명을 입력해 주세요.';
    if (!email) return '이메일 주소를 입력해 주세요.';
    if (!/^\S+@\S+\.\S+$/.test(email)) return '올바른 이메일 주소를 입력해 주세요.';
    if (!phone) return '연락처를 입력해 주세요.';
    if (!targetType) return '제보대상 유형을 선택해 주세요.';
    if (!title) return '제목을 입력해 주세요.';
    if (!content) return '내용을 입력해 주세요.';
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const formEl = e.currentTarget;
    const fd = new FormData(formEl);

    const err = validate(fd);
    if (err) { alert(err); return; }

    try {
      setSubmitting(true);
      const res = await fetch(`${API}/api/reports/realname`, {
        method: 'POST',
        body: fd, // multipart/form-data (브라우저가 헤더 자동 설정)
      });
      if (!res.ok) {
        const text = await res.text();
        alert(`제출 실패\n${text}`);
        return;
      }
      alert('제보가 접수되었습니다. 감사합니다.');
      formEl.reset();
      window.scrollTo(0, 0);
    } catch (e1) {
      alert('네트워크 오류: ' + e1.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
      <PageContainer>
        <Container>
          <AnimatedSection ref={headerRef} isVisible={headerVisible} animation="fadeInUp">
            <PageHeader>
              <PageTitle>실명 제보</PageTitle>
              <PageSubtitle>
                실명으로 제보하시면 보다 신속하고 정확한 조사가 가능합니다. 입력하신 내용은 엄격히 비밀이 보장됩니다.
              </PageSubtitle>
            </PageHeader>
          </AnimatedSection>

          <form onSubmit={onSubmit} encType="multipart/form-data">
            {/* 제보자 정보 */}
            <SectionBox>
              <SectionTitle>제보자 정보</SectionTitle>
              <FormGroup>
                <Label>성명<span className="required">*</span></Label>
                <Input name="reporterName" type="text" placeholder="이름을 입력하세요" autoComplete="name" />
              </FormGroup>
              <FormGroup>
                <Label>이메일 주소<span className="required">*</span></Label>
                <Input name="reporterEmail" type="email" placeholder="example@email.com" autoComplete="email" />
              </FormGroup>
              <FormGroup>
                <Label>연락처<span className="required">*</span></Label>
                <Input name="reporterPhone" type="tel" placeholder="010-1234-5678" autoComplete="tel" />
              </FormGroup>
            </SectionBox>

            {/* 제보대상 정보 */}
            <SectionBox>
              <SectionTitle>제보대상 정보</SectionTitle>
              <FormGroup>
                <Label>제보대상 유형<span className="required">*</span></Label>
                <RadioGroup>
                  <RadioLabel><input type="radio" name="targetType" value="임직원" /> 임직원</RadioLabel>
                  <RadioLabel><input type="radio" name="targetType" value="협력업체" /> 협력업체</RadioLabel>
                  <RadioLabel><input type="radio" name="targetType" value="기타" /> 기타</RadioLabel>
                </RadioGroup>
              </FormGroup>
              <FormGroup>
                <Label>제보 대상자</Label>
                <Input name="targetName" type="text" />
              </FormGroup>
              <FormGroup>
                <Label>부서/업무</Label>
                <Input name="targetDept" type="text" />
              </FormGroup>
              <FormGroup>
                <Label>사건 주요내용</Label>
                <Input name="incidentSummary" type="text" />
              </FormGroup>
            </SectionBox>

            {/* 제보 내용 */}
            <SectionBox>
              <SectionTitle>제보내용</SectionTitle>
              <FormGroup>
                <Label>제목<span className="required">*</span></Label>
                <Input name="title" type="text" placeholder="제목을 입력하세요" />
              </FormGroup>
              <FormGroup>
                <Label>내용<span className="required">*</span></Label>
                <TextArea name="content" placeholder="사실에 근거한 내용을 육하원칙에 따라 구체적으로 작성해주세요" />
              </FormGroup>
              <FormGroup>
                <Label>첨부파일</Label>
                <FileInput name="file" type="file" />
                <small>* 1개, 50MB 이하 문서, 그림, 압축파일 가능</small>
              </FormGroup>
              <FormGroup>
                <Label>소속회사와의 관계</Label>
                <RadioGroup>
                  <RadioLabel><input type="radio" name="relation" value="임직원" /> 임직원</RadioLabel>
                  <RadioLabel><input type="radio" name="relation" value="협력업체" /> 협력업체</RadioLabel>
                  <RadioLabel><input type="radio" name="relation" value="고객" /> 고객</RadioLabel>
                  <RadioLabel><input type="radio" name="relation" value="기타" /> 기타</RadioLabel>
                </RadioGroup>
              </FormGroup>
            </SectionBox>

            <SubmitButtonWrapper>
              <SubmitButton type="submit" disabled={submitting}>
                {submitting ? '등록 중...' : '등록하기'}
              </SubmitButton>
            </SubmitButtonWrapper>
          </form>
        </Container>
      </PageContainer>
  );
}

export default RealReport;
